import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Smartphone, Monitor, ExternalLink, Rocket, Loader2, ArrowLeft,
  Pencil, Save, X, Check, Sparkles, Image as ImageIcon, Layers,
  MessageCircle, Copy, PartyPopper, MapPin, Radio, Palette, AlertCircle, Link2,
} from "lucide-react";
import { api, siteHtmlUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MapPinPicker from "@/components/MapPinPicker";
import LogoEditor from "@/components/LogoEditor";
import LinksSheet from "@/components/LinksSheet";

const THEME_LABELS = {
  modern_luxe: "Modern Luxe", warm_organic: "Warm Organic",
  clean_clinical: "Clean Clinical", energetic_volt: "Energetic Volt",
};

function clone(o) { return JSON.parse(JSON.stringify(o)); }

function setPath(obj, path, value) {
  const parts = path.split(".");
  let o = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = /^\d+$/.test(parts[i]) ? Number(parts[i]) : parts[i];
    if (o[k] == null) return;
    o = o[k];
  }
  const last = parts[parts.length - 1];
  o[/^\d+$/.test(last) ? Number(last) : last] = value;
}

export default function Preview() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [site, setSite] = useState(null);
  const [variantIndex, setVariantIndex] = useState(0);
  const [viewport, setViewport] = useState("mobile");
  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [picker, setPicker] = useState({ open: false, path: null, images: [] });
  const [sectionCatalog, setSectionCatalog] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [logoOpen, setLogoOpen] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  const [urlCheck, setUrlCheck] = useState({ status: "idle", msg: "" });

  // Escape key closes any open sheet
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (linksOpen) setLinksOpen(false);
      else if (logoOpen) setLogoOpen(false);
      else if (mapOpen) setMapOpen(false);
      else if (sheetOpen) setSheetOpen(false);
      else if (picker.open) setPicker({ open: false, path: null, images: [] });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [linksOpen, logoOpen, mapOpen, sheetOpen, picker.open]);
  const [mapForm, setMapForm] = useState({ mode: "auto", url: "", lat: null, lng: null });
  const [savingMap, setSavingMap] = useState(false);
  const [published, setPublished] = useState(false);

  const draftRef = useRef(null);
  const iframeRef = useRef(null);
  const activeChipRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/sites/${siteId}`);
      setSite(data.site);
      const idx = data.site.selected_variant || 0;
      setVariantIndex(idx);
      draftRef.current = clone(data.site.variants?.[idx] || data.site.generated_content);
      const biz = data.site.variants?.[idx]?.business || {};
      setMapForm({ mode: biz.map_mode || "auto", url: biz.map_url || "",
                   lat: biz.map_lat ?? null, lng: biz.map_lng ?? null });
    } catch {
      toast.error("Site not found");
      navigate("/dashboard");
    }
  }, [siteId, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setSectionCatalog(data.sections || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editMode) activeChipRef.current?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [variantIndex, editMode, site]);

  // Listen for inline edits coming from the iframe (same-origin)
  useEffect(() => {
    function onMsg(e) {
      if (e.origin !== window.location.origin) return;
      const d = e.data;
      if (!d || !d.wt) return;
      if (d.wt === "text") {
        if (draftRef.current) { setPath(draftRef.current, d.path, d.value); setDirty(true); }
      } else if (d.wt === "image") {
        openPicker(d.path);
      }
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
    // eslint-disable-next-line
  }, []);

  const openPicker = async (path) => {
    setPicker({ open: true, path, images: [] });
    try {
      const cat = site?.category || (await api.get(`/sites/${siteId}`)).data.site.category;
      const { data } = await api.get(`/assets/${cat}`);
      setPicker({ open: true, path, images: data.images });
    } catch {
      setPicker({ open: true, path, images: [] });
    }
  };

  const chooseImage = (url) => {
    const path = picker.path;
    if (draftRef.current) { setPath(draftRef.current, path, url); setDirty(true); }
    const doc = iframeRef.current?.contentDocument;
    if (doc) {
      if (path === "images.hero") {
        const h = doc.getElementById("wt-hero");
        const ov = h?.getAttribute("data-ov") || "rgba(0,0,0,0.5)";
        if (h) h.style.backgroundImage = `linear-gradient(${ov},${ov}),url('${url}')`;
      } else {
        const el = doc.querySelector(`[data-edit-img="${path}"]`);
        if (el) el.src = url;
      }
    }
    setPicker({ open: false, path: null, images: [] });
  };

  const selectVariant = async (i) => {
    if (i === variantIndex) return;
    setVariantIndex(i);
    draftRef.current = clone(site.variants[i]);
    setReloadKey((k) => k + 1);
    try {
      const { data } = await api.patch(`/sites/${siteId}/variant`, { index: i });
      setSite(data.site);
    } catch { /* non-blocking */ }
  };

  const startEdit = () => { draftRef.current = clone(site.variants[variantIndex]); setDirty(false); setEditMode(true); setReloadKey((k) => k + 1); };
  const cancelEdit = () => { draftRef.current = clone(site.variants[variantIndex]); setDirty(false); setEditMode(false); setReloadKey((k) => k + 1); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/sites/${siteId}/content`, { index: variantIndex, content: draftRef.current });
      setSite(data.site);
      setEditMode(false); setDirty(false);
      setReloadKey((k) => k + 1);
      toast.success("Changes saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const currentSections = site?.variants?.[variantIndex]?.sections || [];

  const toggleSection = async (id) => {
    const has = currentSections.includes(id);
    const next = has ? currentSections.filter((s) => s !== id) : [...currentSections, id];
    try {
      const { data } = await api.patch(`/sites/${siteId}/sections`, { index: variantIndex, sections: next });
      setSite(data.site);
      draftRef.current = clone(data.site.variants[variantIndex]);
      setReloadKey((k) => k + 1);
      toast.success(has ? "Section removed" : "Section added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update sections");
    }
  };

  const saveMap = async () => {
    if (mapForm.mode === "pin" && (mapForm.lat == null || mapForm.lng == null)) {
      toast.error("Drop a pin on the map first");
      return;
    }
    setSavingMap(true);
    try {
      const { data } = await api.patch(`/sites/${siteId}/map`, {
        map_mode: mapForm.mode, map_url: mapForm.url,
        map_lat: mapForm.lat, map_lng: mapForm.lng,
      });
      setSite(data.site);
      draftRef.current = clone(data.site.variants[variantIndex]);
      setReloadKey((k) => k + 1);
      setMapOpen(false);
      toast.success("Map location updated across all 3 site variants");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save map");
    } finally { setSavingMap(false); }
  };

  const mapPreviewSrc = (() => {
    if (mapForm.mode === "off") return "";
    if (mapForm.mode === "pin") {
      if (mapForm.lat == null || mapForm.lng == null) return "";
      return `https://www.google.com/maps?q=${mapForm.lat},${mapForm.lng}&z=16&output=embed`;
    }
    if (mapForm.mode === "url") {
      const u = (mapForm.url || "").trim();
      if (!u) return "";
      if (u.includes("google.") && (u.includes("/embed") || u.includes("output=embed"))) return u;
      return `https://www.google.com/maps?q=${encodeURIComponent(u)}&output=embed`;
    }
    const biz = site?.variants?.[variantIndex]?.business || {};
    const parts = [biz.address, biz.city].filter(Boolean).join(", ");
    return parts ? `https://www.google.com/maps?q=${encodeURIComponent(parts)}&output=embed` : "";
  })();

  const publish = async () => {
    if (user?.plan?.status !== "active") {
      toast("Pick a plan to publish this site", { description: "Starter ₹399/mo · Pro ₹999/mo · Studio ₹1499/mo" });
      navigate("/subscribe", { state: { returnTo: `/preview/${siteId}` } });
      return;
    }
    setPublishing(true);
    try {
      await api.post(`/sites/${siteId}/publish`);
      await refreshUser();
      setPublished(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not publish");
    } finally { setPublishing(false); }
  };

  const shareText = (to) => {
    const url = siteHtmlUrl(siteId);
    const msg = `🎉 ${site.business_name} is now online! Check out our new website: ${url}`;
    return to
      ? `https://wa.me/91${to}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
  };
  const copyLink = async () => {
    const url = siteHtmlUrl(siteId);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success("Link copied");
      } catch {
        toast.error("Could not copy — long-press the link to copy it");
      }
    }
  };

  if (!site) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#C9A24B] wt-spin" /></div>;
  }

  const isMobile = viewport === "mobile";
  const src = siteHtmlUrl(siteId, { v: variantIndex, edit: editMode, t: reloadKey });

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[#27272A]">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white inline-flex items-center gap-1.5 text-sm">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
          </button>

          <div className="flex items-center gap-1 bg-[#161616] border border-[#27272A] rounded-xl p-1">
            <button data-testid="preview-viewport-mobile-btn" onClick={() => setViewport("mobile")}
              className={`p-2 rounded-lg transition-all ${isMobile ? "bg-[#C9A24B] text-[#0A0A0A]" : "text-zinc-400"}`}>
              <Smartphone className="w-4 h-4" />
            </button>
            <button data-testid="preview-viewport-desktop-btn" onClick={() => setViewport("desktop")}
              className={`p-2 rounded-lg transition-all ${!isMobile ? "bg-[#C9A24B] text-[#0A0A0A]" : "text-zinc-400"}`}>
              <Monitor className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <button data-testid="preview-cancel-edit-btn" onClick={cancelEdit}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#1C1C1E] border border-[#27272A] text-sm text-zinc-300 hover:border-white/30">
                  <X className="w-4 h-4" /> Cancel
                </button>
                <button data-testid="preview-save-edit-btn" onClick={saveEdit} disabled={saving || !dirty}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] active:scale-[0.98] disabled:opacity-50 transition-all">
                  {saving ? <Loader2 className="w-4 h-4 wt-spin" /> : <Save className="w-4 h-4" />} Save
                </button>
              </>
            ) : (
              <>
                <button data-testid="preview-edit-btn" onClick={startEdit}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#1C1C1E] border border-[#27272A] text-sm text-zinc-200 hover:border-[#C9A24B]/50">
                  <Pencil className="w-4 h-4 text-[#C9A24B]" /> <span className="hidden sm:inline">Edit</span>
                </button>
                <a data-testid="preview-open-newtab" href={siteHtmlUrl(siteId, { v: variantIndex })} target="_blank" rel="noreferrer"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#1C1C1E] border border-[#27272A] text-sm text-zinc-300 hover:border-[#C9A24B]/50">
                  <ExternalLink className="w-4 h-4" />
                </a>
                <button data-testid="preview-approve-btn" onClick={publish} disabled={publishing}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] active:scale-[0.98] transition-all disabled:opacity-60">
                  {publishing ? <Loader2 className="w-4 h-4 wt-spin" /> : <Rocket className="w-4 h-4" />}
                  <span className="hidden sm:inline">{site.status === "live" ? "Re-publish" : "Publish"}</span>
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Variant chooser / edit hint */}
      {editMode ? (
        <div className="bg-[#C9A24B]/10 border-b border-[#C9A24B]/20">
          <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between gap-2 text-sm text-[#E5C378]">
            <span className="flex items-center gap-2 min-w-0">
              <Pencil className="w-4 h-4 shrink-0" />
              <span className="truncate">Tap any <b>text</b> to edit, tap any <b>photo</b> to swap it.</span>
            </span>
            <button data-testid="preview-manage-sections-btn" onClick={() => setSheetOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1C1C1E] border border-[#27272A] text-zinc-200 hover:border-[#C9A24B]/50 transition-all">
              <Layers className="w-4 h-4 text-[#C9A24B]" /> Sections
            </button>
            <button data-testid="map-settings-trigger-btn" onClick={() => setMapOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1C1C1E] border border-[#27272A] text-zinc-200 hover:border-[#C9A24B]/50 transition-all">
              <MapPin className="w-4 h-4 text-[#C9A24B]" /> Map
            </button>
            <button data-testid="logo-editor-trigger-btn" onClick={() => setLogoOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1C1C1E] border border-[#27272A] text-zinc-200 hover:border-[#C9A24B]/50 transition-all">
              <Palette className="w-4 h-4 text-[#C9A24B]" /> Logo
            </button>
            <button data-testid="links-sheet-trigger-btn" onClick={() => setLinksOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1C1C1E] border border-[#27272A] text-zinc-200 hover:border-[#C9A24B]/50 transition-all">
              <Link2 className="w-4 h-4 text-[#C9A24B]" /> Links
            </button>
          </div>
        </div>
      ) : (
        <div className="border-b border-[#27272A] bg-[#0d0d0d]">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3 overflow-x-auto">
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold shrink-0 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-[#C9A24B]" /> Choose a design
            </span>
            {(site.variants || []).map((v, i) => (
              <button key={i} data-testid={`preview-variant-${i}`} onClick={() => selectVariant(i)}
                ref={i === variantIndex ? activeChipRef : null}
                className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all ${i === variantIndex ? "border-[#C9A24B] bg-[#C9A24B]/10 text-[#E5C378]" : "border-[#27272A] text-zinc-400 hover:border-[#C9A24B]/40"}`}>
                {i === variantIndex && <Check className="w-3.5 h-3.5" />}
                Option {i + 1}
                <span className="text-xs text-zinc-500">· {THEME_LABELS[v.theme_id] || v.theme_id}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Preview frame */}
      <div className="flex-1 bg-[#080808] py-6 sm:py-10 px-4 flex justify-center">
        <div className={`transition-all duration-300 ${isMobile ? "w-[380px] max-w-full" : "w-full max-w-5xl"}`}>
          <div className="bg-black rounded-2xl overflow-hidden border border-[#27272A] shadow-2xl">
            <div className="h-8 bg-[#161616] flex items-center gap-1.5 px-4 border-b border-[#27272A]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#ef4444]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#f59e0b]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#22c55e]" />
              <span className="ml-3 text-[11px] text-zinc-500 truncate">{site.subdomain}.webtu.in</span>
            </div>
            <iframe
              key={`${variantIndex}-${editMode}-${reloadKey}`}
              ref={iframeRef}
              data-testid="preview-iframe"
              title="site-preview"
              src={src}
              className="w-full bg-white"
              style={{ height: isMobile ? 640 : 720, border: "none" }}
            />
          </div>
        </div>
      </div>

      {/* Sections manager */}
      {sheetOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" data-testid="preview-sections-sheet"
          onClick={() => setSheetOpen(false)}>
          <div className="bg-[#161616] border-l border-[#27272A] w-full max-w-sm h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl font-bold flex items-center gap-2"><Layers className="w-5 h-5 text-[#C9A24B]" /> Sections</h3>
              <button onClick={() => setSheetOpen(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-zinc-400 mb-5">Turn whole sections on or off for this design.</p>
            <div className="space-y-2.5">
              {sectionCatalog.map((sec) => {
                const on = currentSections.includes(sec.id);
                return (
                  <div key={sec.id} data-testid={`preview-section-toggle-${sec.id}`}
                    className={`flex items-center justify-between p-3.5 rounded-xl border transition-all ${on ? "border-[#C9A24B]/40 bg-[#C9A24B]/5" : "border-[#27272A] bg-[#0A0A0A]"}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{sec.label}</span>
                      {sec.mandatory && <span className="text-[10px] uppercase tracking-wider text-zinc-500">Required</span>}
                    </div>
                    <button disabled={sec.mandatory} onClick={() => toggleSection(sec.id)}
                      className={`relative w-11 h-6 rounded-full transition-all ${on ? "bg-[#C9A24B]" : "bg-[#3a3a3d]"} ${sec.mandatory ? "opacity-40 cursor-not-allowed" : ""}`}>
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Map settings sheet */}
      {mapOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" data-testid="map-settings-sheet"
          onClick={() => setMapOpen(false)}>
          <div className="bg-[#161616] border-l border-[#27272A] w-full max-w-md h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl font-bold flex items-center gap-2"><MapPin className="w-5 h-5 text-[#C9A24B]" /> Shop Map & Location</h3>
              <button onClick={() => setMapOpen(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-zinc-400 mb-5">Choose how customers see your location. Applies to all 3 designs.</p>

            <div className="space-y-2.5 mb-5">
              {[
                { id: "auto", label: "Auto from Shop Address", desc: "Uses your address & city — no setup needed" },
                { id: "pin", label: "Drop a Pin on the Map", desc: "Pinpoint your exact shop — most accurate" },
                { id: "url", label: "Paste Custom Google Maps URL", desc: "Use an exact share or embed link" },
                { id: "off", label: "Turn Off Map", desc: "Hide map from the website" },
              ].map((opt) => {
                const on = mapForm.mode === opt.id;
                return (
                  <button key={opt.id} data-testid={`map-mode-${opt.id}-radio`}
                    onClick={() => setMapForm((f) => ({ ...f, mode: opt.id }))}
                    className={`w-full text-left flex items-start gap-3 p-3.5 rounded-xl border transition-all ${on ? "border-[#C9A24B] bg-[#C9A24B]/8" : "border-[#27272A] bg-[#0A0A0A] hover:border-[#3a3a3d]"}`}>
                    <span className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${on ? "border-[#C9A24B]" : "border-zinc-600"}`}>
                      {on && <span className="w-2 h-2 rounded-full bg-[#C9A24B]" />}
                    </span>
                    <div className="min-w-0">
                      <div className={`text-sm font-semibold ${on ? "text-[#E5C378]" : "text-zinc-200"}`}>{opt.label}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{opt.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {mapForm.mode === "pin" && (
              <div className="mb-5">
                <MapPinPicker
                  lat={mapForm.lat} lng={mapForm.lng}
                  seedQuery={[
                    site?.variants?.[variantIndex]?.business?.address,
                    site?.variants?.[variantIndex]?.business?.city,
                  ].filter(Boolean).join(", ")}
                  onChange={({ lat, lng }) => setMapForm((f) => ({ ...f, lat, lng }))}
                />
              </div>
            )}

            {mapForm.mode === "url" && (
              <div className="mb-5">
                <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Google Maps share or embed link</label>
                <input data-testid="map-custom-url-input" value={mapForm.url}
                  onChange={(e) => { setMapForm((f) => ({ ...f, url: e.target.value })); setUrlCheck({ status: "idle", msg: "" }); }}
                  onBlur={async () => {
                    const u = (mapForm.url || "").trim();
                    if (!u) return setUrlCheck({ status: "idle", msg: "" });
                    if (!/^https?:\/\//i.test(u)) {
                      return setUrlCheck({ status: "err", msg: "Paste a full https:// Google Maps link." });
                    }
                    setUrlCheck({ status: "checking", msg: "Checking link…" });
                    try {
                      const { data } = await api.post("/util/resolve-url", { url: u });
                      if (!data.is_google_maps) setUrlCheck({ status: "err", msg: "This doesn't look like a Google Maps link." });
                      else if (!data.embeddable) setUrlCheck({ status: "warn", msg: "This link may not embed cleanly — for best results paste an /maps/embed link or use Pin mode." });
                      else setUrlCheck({ status: "ok", msg: "Looks good — link is embeddable." });
                    } catch { setUrlCheck({ status: "warn", msg: "Could not verify link." }); }
                  }}
                  placeholder="https://maps.app.goo.gl/... or /maps/embed?..."
                  className="mt-2 w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3 text-sm text-zinc-100 focus:outline-none focus:border-[#C9A24B] transition-all" />
                {urlCheck.msg && (
                  <div data-testid="map-url-check" className={`mt-2 text-xs inline-flex items-center gap-1.5 ${urlCheck.status === "ok" ? "text-[#22C55E]" : urlCheck.status === "err" ? "text-[#EF4444]" : "text-[#F59E0B]"}`}>
                    {urlCheck.status === "checking" ? <Loader2 className="w-3.5 h-3.5 wt-spin" /> : <AlertCircle className="w-3.5 h-3.5" />}
                    {urlCheck.msg}
                  </div>
                )}
                <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
                  Open Google Maps → search your shop → tap <b>Share</b> → <b>Copy link</b> → paste it here.
                </p>
              </div>
            )}

            {mapForm.mode !== "off" && (
              <div className="mb-6">
                <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-2 flex items-center gap-1.5">
                  <Radio className="w-3 h-3" /> Live preview
                </div>
                <div className="rounded-xl overflow-hidden border border-[#27272A] bg-[#0A0A0A] aspect-video">
                  {mapPreviewSrc ? (
                    <iframe key={mapPreviewSrc} data-testid="map-live-preview" title="map preview" src={mapPreviewSrc}
                      className="w-full h-full transition-opacity duration-300"
                      loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-zinc-500 text-center px-4">
                      {mapForm.mode === "url" ? "Paste a link to preview" : "Add a shop address on the site to auto-generate a map"}
                    </div>
                  )}
                </div>
              </div>
            )}

            <button data-testid="map-settings-save-btn" onClick={saveMap} disabled={savingMap}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] font-bold py-3.5 rounded-xl transition-all active:scale-[0.98] disabled:opacity-60">
              {savingMap ? <Loader2 className="w-4 h-4 wt-spin" /> : <Save className="w-4 h-4" />} Save Map Settings
            </button>
          </div>
        </div>
      )}

      {/* Publish success + WhatsApp delivery */}
      {published && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" data-testid="preview-publish-modal">
          <div className="bg-[#161616] border border-[#27272A] rounded-3xl w-full max-w-md p-7 text-center wt-fade">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#C9A24B] to-[#E5C378] flex items-center justify-center mb-5">
              <PartyPopper className="w-8 h-8 text-[#0A0A0A]" />
            </div>
            <h2 className="font-display text-2xl font-bold">You're live! 🎉</h2>
            <p className="text-zinc-400 text-sm mt-2 mb-5"><b className="text-[#E5C378]">{site.business_name}</b> is now online. Send the link to yourself and share it with customers.</p>

            <div className="flex items-center gap-2 bg-[#0A0A0A] border border-[#27272A] rounded-xl px-3 py-2.5 mb-5">
              <span className="text-xs text-zinc-400 truncate flex-1 text-left">{site.subdomain}.webtu.in</span>
              <button data-testid="publish-copy-link" onClick={copyLink} className="text-zinc-400 hover:text-[#C9A24B]"><Copy className="w-4 h-4" /></button>
            </div>

            <a data-testid="publish-send-whatsapp" href={shareText(user?.phone)} target="_blank" rel="noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#22bf5b] text-[#04120a] font-bold py-3.5 rounded-xl transition-all active:scale-[0.98] mb-2.5">
              <MessageCircle className="w-5 h-5" /> Send link to my WhatsApp
            </a>
            <a data-testid="publish-share-whatsapp" href={shareText(null)} target="_blank" rel="noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1C1C1E] border border-[#27272A] text-zinc-200 hover:border-[#C9A24B]/50 font-semibold py-3 rounded-xl transition-all mb-2.5">
              <MessageCircle className="w-4 h-4 text-[#25D366]" /> Share with customers
            </a>
            <button data-testid="publish-goto-dashboard" onClick={() => navigate("/dashboard")}
              className="w-full text-sm text-zinc-400 hover:text-white py-2">Go to dashboard</button>
          </div>
        </div>
      )}

      {logoOpen && (
        <LogoEditor siteId={siteId} site={site} onClose={() => setLogoOpen(false)}
          onChange={(s) => { setSite(s); setReloadKey((k) => k + 1); }} />
      )}
      {linksOpen && (
        <LinksSheet siteId={siteId} site={site} onClose={() => setLinksOpen(false)}
          onChange={(s) => { setSite(s); setReloadKey((k) => k + 1); }} />
      )}

      {/* Photo picker */}
      {picker.open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setPicker({ open: false, path: null, images: [] })} data-testid="preview-photo-picker">
          <div className="bg-[#161616] border border-[#27272A] rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-[#27272A] flex items-center justify-between">
              <h3 className="font-display font-bold flex items-center gap-2"><ImageIcon className="w-4 h-4 text-[#C9A24B]" /> Choose a photo</h3>
              <button onClick={() => setPicker({ open: false, path: null, images: [] })} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-3">
              {picker.images.length === 0 ? (
                <div className="col-span-full py-10 flex justify-center"><Loader2 className="w-6 h-6 text-[#C9A24B] wt-spin" /></div>
              ) : picker.images.map((url, i) => (
                <button key={i} data-testid={`preview-photo-option-${i}`} onClick={() => chooseImage(url)}
                  className="rounded-xl overflow-hidden border border-[#27272A] hover:border-[#C9A24B] transition-all aspect-[4/3] bg-[#0A0A0A]">
                  <img src={url} alt="option" loading="lazy"
                    onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
                    className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
